/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import config from '../config';

import { setSelectedDivisions } from '../settings/state/settings.actions';

import { safeExtractFromObject } from '../helpers/helpers';

import colors from '../constants/Colors';

export const colorClasses = [
  'division-block-blue',
  'division-block-orange',
  'division-block-green',
  'division-block-yellow',
  'division-block-purple',
];

function DivisionsDropdown() {
  const dispatch = useDispatch();
  const divisions = useSelector((state) => state.settings.divisions);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));

  const hasRedesign = settings?.uxRedesignPt1;

  // Divisions is an object with division IDs as key
  const divisionIds = Object.keys(divisions);

  const [hovering, setHovering] = useState(false);

  const onMouseEnter = useCallback(() => setHovering(true), []);
  const onMouseLeave = useCallback(() => setHovering(false), []);

  const onChange = useCallback((newSelected) => {
    dispatch(setSelectedDivisions(new Set(newSelected)));
  }, [dispatch]);
  const onSelectAll = useCallback(() => {
    dispatch(setSelectedDivisions(new Set(divisionIds)));
  }, [dispatch, divisionIds]);

  const selectedIDs = useMemo(() => Array.from(selectedDivisions), [selectedDivisions]);

  // New design logic for hasRedesign
  const [isOpen, setIsOpen] = useState(false);

  const getVisibleDivisions = useCallback(() => {
    if (selectedIDs.length === 0) return [];

    // Start with 2 divisions and reduce if needed
    let visibleCount = Math.min(2, selectedIDs.length);

    // If we have more than 2, check if we can fit the badge
    if (selectedIDs.length > 2) {
      // Try with 2 divisions first
      const testDivisions = selectedIDs.slice(0, 2);
      const totalWidth = testDivisions.length * 140 + 50; // 140px per division + ~50px for badge

      // If it's too wide, try with 1 division
      if (totalWidth > 400) { // Increased threshold to be less aggressive
        visibleCount = 1;
      }
    }

    return selectedIDs.slice(0, visibleCount);
  }, [selectedIDs]);

  const visibleDivisions = getVisibleDivisions();
  const remainingCount = selectedIDs.length - visibleDivisions.length;

  const handleDivisionClick = useCallback((id) => {
    if (selectedIDs.includes(id)) {
      const newSelected = selectedIDs.filter(
        (selectedId) => selectedId !== id,
      );
      dispatch(setSelectedDivisions(new Set(newSelected)));
    } else {
      const newSelected = [...selectedIDs, id];
      dispatch(setSelectedDivisions(new Set(newSelected)));
    }
  }, [selectedIDs, dispatch]);

  const handleDivisionClose = useCallback((id) => {
    const newSelected = selectedIDs.filter(
      (selectedId) => selectedId !== id,
    );
    dispatch(setSelectedDivisions(new Set(newSelected)));
  }, [selectedIDs, dispatch]);

  const renderDivisionTag = useCallback((id, index, showClose = false) => {
    const division = divisions[id];
    if (!division) return null;

    return (
      <div
        key={id}
        className={colorClasses[index % colorClasses.length]}
      >
        <span className="division-text">
          {division.name}
        </span>
        {showClose && selectedIDs.length > 1 && (
          <CloseOutlined
            style={{ fontSize: 10, cursor: 'pointer', flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleDivisionClose(id);
            }}
          />
        )}
      </div>
    );
  }, [divisions, selectedIDs, colorClasses, handleDivisionClose]);

  const labelView = useMemo(() => {
    if (selectedIDs.length >= 5) {
      return (
        <div
          className={
            hasRedesign
              ? 'divisions-label-many-redesign'
              : 'divisions-label-many'
          }
        >
          {`${selectedIDs.length} divisions selected`}
        </div>
      );
    }

    return (
      <div className="divisions-label-container">
        {selectedIDs.map((id) => {
          const {
            [id]: {
              name,
            } = {},
          } = divisions;
          return name
            ? (
              <div key={id}>
                <div
                  className={
                    hasRedesign
                      ? 'divisions-label-redesign'
                      : 'divisions-label'
                  }
                  key={id}
                >
                  {name}
                </div>
                <br />
              </div>
            )
            : null;
        })}
      </div>
    );
  }, [selectedIDs, divisions, hasRedesign]);

  const tagRender = useCallback((row) => (
    <Tag
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...row}
      closable={selectedIDs.length > 1}
      className={
        hasRedesign
          ? 'division-tag-redesign'
          : 'division-tag'
      }
      closeIcon={
        <CloseOutlined style={{ color: 'white', fontSize: 10 }} />
      }
    >
      <span
        className={
          selectedIDs.length > 1
            ? 'division-tag-label-closeable'
            : 'division-tag-label-uncloseable'
        }
      >
        {row.label}
      </span>
    </Tag>
  ), [selectedIDs, hasRedesign]);

  const renderNewDesign = useCallback(() => (
    <div
      style={{
        position: 'relative',
        flex: '1 0 0',
      }}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="divisions-input-field"
      >
        {selectedIDs.length === 0 ? (
          <div className="divisions-input-placeholder">
            Select division(s)
          </div>
        ) : (
          <>
            {visibleDivisions.map((id, index) => renderDivisionTag(id, index))}
            {remainingCount > 0 && (
              <div className="division-badge">
                +{remainingCount}
              </div>
            )}
          </>
        )}
      </div>

      {hovering && !isOpen && selectedIDs.length > 2 && (
        <div className="divisions-hover-dropdown">
          {selectedIDs.slice(2).map((id, index) => renderDivisionTag(id, index + 2))}
        </div>
      )}

      {isOpen && (
        <div className="divisions-dropdown-menu">
          <div className="divisions-dropdown-content">
            {Object.values(divisions).map(({ id, name }, index) => (
              <div
                key={id}
                className={`division-dropdown-item ${colorClasses[index % colorClasses.length]}`}
                onClick={() => handleDivisionClick(id)}
              >
                {selectedIDs.includes(id) && (
                  <CloseOutlined
                    style={{ fontSize: 10, cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDivisionClose(id);
                    }}
                  />
                )}
                <span className="division-dropdown-item-text">{name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ), [
    selectedIDs,
    visibleDivisions,
    remainingCount,
    renderDivisionTag,
    hovering,
    isOpen,
    divisions,
    colorClasses,
    handleDivisionClick,
    handleDivisionClose,
  ]);

  const renderHoveringContent = useMemo(() => {
    if (hasRedesign) {
      return renderNewDesign();
    }
    return (
      <Select
        className="divisions-dropdown"
        mode="multiple"
        value={selectedIDs}
        open
        dropdownMatchSelectWidth={false}
        onChange={onChange}
        filterOption={(input, option) => (
          option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0
        )}
        tagRender={tagRender}
      >
        {
          Object.values(divisions).map(({ id, name }) => (
            <Select.Option
              key={id}
              title={name}
              value={id}
              disabled={selectedIDs.length === 1 && selectedIDs[0] === id}
              className={
                hasRedesign ? 'divisions-dropdown-select-redesign' : ''
              }
            >
              {name}
            </Select.Option>
          ))
        }
      </Select>
    );
  }, [hasRedesign, renderNewDesign, selectedIDs, onChange, divisions, tagRender]);

  const renderNonHoveringContent = useMemo(() => {
    if (hasRedesign) {
      return renderNewDesign();
    }
    return labelView;
  }, [hasRedesign, renderNewDesign, labelView]);

  if (divisionIds.length <= 1 || !config.showDivisions) return null;
  return (
    <div
      id="divisions-dropdown-container"
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      style={
        hasRedesign
          ? {
            paddingLeft: 15,
            paddingRight: 15,
            borderTopColor: colors.REDESIGN_BUTTON_UNSELECTED_TEXT,
          }
          : {}
      }
    >
      <div id="divisions-header-container">
        <div id="divisions-header-title">
          Divisions:
        </div>
        {
        hovering
          && (
            <div
              id={
                hasRedesign
                  ? 'divisions-all-button-redesign'
                  : 'divisions-all-button'
              }
              onClick={onSelectAll}
            >
              Select All
            </div>
          )
        }
      </div>
      {
      hovering
        ? renderHoveringContent
        : renderNonHoveringContent
      }
    </div>
  );
}

export default DivisionsDropdown;
